/**
 * Reportes → Turnos (`/reportes/turnos`).
 *
 * Puerto de `Liquidaciones::index` + `Liquidaciones::obtener_turnos_liquidacion`
 * + `Liquidaciones_model::obtener_turnos_liquidacion` y su cuadre
 * (`calcularResumenFinanciero`) + `views/menu/liquidaciones/index.php`.
 *
 * La pantalla del panel anterior tenía: el selector de fecha con *Generar
 * Reporte*, cuatro tarjetas de **estadísticas del día**, una barra de
 * herramientas (estadísticas, consolidado, PDF, productividad, cálculos,
 * verificar, deshacer y exportar transacciones) y, agrupados por **tipo de turno**,
 * los turnos del día en tarjetas con el resumen financiero, los desgloses, las
 * bóvedas, los gastos y el estado de verificación.
 *
 * Este entrega cubre el **día completo**: estadísticas, turnos agrupados por tipo
 * de turno con su cuadre (efectivo esperado y faltante o sobrante), el detalle de
 * las transacciones de cada turno, la verificación (una y masiva), su reverso, la
 * exportación del día y los **documentos del turno**: el ticket de cierre y el
 * parte diario del día, los dos en PDF y generados por la API del panel.
 *
 * Reglas conservadas:
 * - Los turnos se agrupan por **tipo de turno** según la hora de llegada y las
 *   categorías del sistema (30 minutos de tolerancia al inicio y turnos que cruzan
 *   la medianoche), como el original.
 * - El **cuadre** es el mismo: ventas + lubricantes + ingresos adicionales −
 *   bóvedas − gastos − cobros que no son efectivo − descuentos netos −
 *   créditos netos − consumo interno − canjes CardPlus − pagos adelantados, y el
 *   faltante es ese efectivo menos lo entregado por el trabajador (que aquí ya no se
 *   muestra: en la operación actual siempre queda en cero). El cálculo y los totales
 *   por serie viven en la API (mismo criterio que el modelo anterior).
 * - Un sobrante o faltante por debajo del **monto mínimo configurado** cuenta como
 *   cuadrado, como el «monto mínimo faltante» del sistema.
 * - Los botones de verificación sólo aparecen con el **permiso** correspondiente
 *   (`liquidaciones.verificar`, `…verificar_masivo`, `…deshacer_verificacion`,
 *   `…deshacer_masivo`), como en el original.
 *
 * Diferencias anotadas: la verificación individual pide el **efectivo recibido**
 * (el original abría un formulario con el detalle de cada método de pago, que llega
 * en la siguiente entrega), se añade el atajo de días con turnos y el **cambio de
 * comprobante** de una transacción (los *canjes* del módulo anterior) todavía no
 * tiene pantalla aquí.
 */

import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  BadgePercent,
  Calculator,
  Check,
  CheckCircle2,
  CreditCard,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Minus,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  TrendingUp,
  TriangleAlert,
  Users,
  Vault,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import {
  cn,
  formatCurrency,
  formatHoraValue,
  formatNumber,
  formatPaymentMethodName,
} from '@/lib/utils';

import {
  reportesApi,
  useEstadisticasLiquidacion,
  usePermisosLiquidacion,
  useTurnosDelDia,
  type TurnoLiquidacion,
} from '../reportes.api';
import { TurnoAjustesDialog } from '../components/turno-ajustes-dialog';
import { TurnoTransaccionesDialog } from '../components/turno-transacciones-dialog';
import { TurnoCalculosDialog } from '../components/turno-calculos-dialog';
import { TurnoConsolidadoDialog } from '../components/turno-consolidado-dialog';
import { TurnoMetodosPagoDialog } from '../components/turno-metodos-pago-dialog';

/** Fecha local `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Hora `HH:mm` de una marca de tiempo. */
function hora(valor: string | null | undefined): string {
  if (!valor) return '—';

  return formatHoraValue(valor).slice(0, 5);
}

/** Diferencia del turno: cuadrado, faltante o sobrante (con la tolerancia dada). */
function diferencia(turno: TurnoLiquidacion, tolerancia: number) {
  const pendiente = Number(turno.resumen_financiero?.efectivo_pendiente ?? 0);

  if (Math.abs(pendiente) <= tolerancia) return { tipo: 'cuadrado' as const, monto: Math.abs(pendiente) };
  if (pendiente > 0) return { tipo: 'faltante' as const, monto: pendiente };

  return { tipo: 'sobrante' as const, monto: Math.abs(pendiente) };
}

/** Nombre del usuario del turno. */
function nombreDe(turno: TurnoLiquidacion): string {
  return `${turno.first_name ?? ''} ${turno.last_name ?? ''}`.trim() || `Usuario ${turno.usuario_id}`;
}

/** Estado de verificación como etiqueta. */
function estadoDeVerificacion(turno: TurnoLiquidacion) {
  if (Number(turno.estado_verificacion) === 2) return { texto: 'Verificado', tono: 'success' as const };
  if (Number(turno.estado_verificacion) === 3) return { texto: 'Rechazado', tono: 'danger' as const };

  return { texto: 'Pendiente', tono: 'warning' as const };
}

/**
 * Colores del **cuadre del día**.
 *
 * El faltante o el sobrante es lo primero que se mira al cerrar el día, así que su tarjeta
 * va con fondo y borde de color (los mismos tonos que la tarjeta de cada turno: rojo para
 * el faltante, ámbar para el sobrante y verde cuando el día cuadra).
 */
const TONOS_CUADRE = {
  neutro: {
    caja: '',
    etiqueta: 'text-ink-500',
    valor: 'text-ink-900',
    detalle: 'text-ink-500',
    icono: null,
  },
  faltante: {
    caja: 'border-rose-300 bg-rose-50',
    etiqueta: 'text-rose-700',
    valor: 'text-rose-800',
    detalle: 'text-rose-700',
    icono: <TriangleAlert className="size-3.5" aria-hidden="true" />,
  },
  sobrante: {
    caja: 'border-amber-300 bg-amber-50',
    etiqueta: 'text-amber-800',
    valor: 'text-amber-900',
    detalle: 'text-amber-800',
    icono: <TrendingUp className="size-3.5" aria-hidden="true" />,
  },
  cuadrado: {
    caja: 'border-emerald-300 bg-emerald-50',
    etiqueta: 'text-emerald-700',
    valor: 'text-emerald-800',
    detalle: 'text-emerald-700',
    icono: <CheckCircle2 className="size-3.5" aria-hidden="true" />,
  },
} as const;

export function ReporteTurnosPage() {
  useDocumentTitle('Reportes · Turnos');

  const [fecha, setFecha] = useState(() => fechaLocal(new Date()));
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [turnoTransacciones, setTurnoTransacciones] = useState<TurnoLiquidacion | null>(null);
  const [turnoCalculos, setTurnoCalculos] = useState<TurnoLiquidacion | null>(null);
  const [turnoAjustes, setTurnoAjustes] = useState<TurnoLiquidacion | null>(null);
  const [turnoMetodos, setTurnoMetodos] = useState<TurnoLiquidacion | null>(null);
  const [turnoAVerificar, setTurnoAVerificar] = useState<TurnoLiquidacion | null>(null);
  const [efectivoRecibido, setEfectivoRecibido] = useState('');
  const [detalle, setDetalle] = useState('');
  const [consolidadoAbierto, setConsolidadoAbierto] = useState(false);
  const [configAbierta, setConfigAbierta] = useState(false);
  const [montoMinimo, setMontoMinimo] = useState('');

  const consulta = useTurnosDelDia(fecha);
  const estadisticas = useEstadisticasLiquidacion(fecha);
  const permisos = usePermisosLiquidacion();
  const configuracion = useQuery({
    queryKey: ['reportes', 'turnos', 'configuracion'],
    queryFn: ({ signal }) => reportesApi.configuracionLiquidacion(signal),
    staleTime: 300_000,
  });

  // El margen para dar un turno por cuadrado es el «monto mínimo faltante» de la
  // empresa, igual que en el panel anterior (por debajo de ese importe no se
  // informa como faltante).
  const tolerancia = Number(configuracion.data?.monto_minimo_faltante ?? 0);

  const turnos = useMemo(() => consulta.data ?? [], [consulta.data]);
  const metodosNoEfectivo = useMemo(() => {
    const acumulado = new Map<string, { metodo: string; monto: number }>();

    for (const turno of turnos) {
      for (const metodo of turno.resumen_financiero?.metodos_pago_no_efectivo ?? []) {
        const clave = metodo.metodo.trim().toLocaleLowerCase('es-PE');
        const registro = acumulado.get(clave) ?? {
          metodo: formatPaymentMethodName(metodo.metodo),
          monto: 0,
        };
        registro.monto += metodo.monto;
        acumulado.set(clave, registro);
      }
    }

    return [...acumulado.values()].sort((a, b) => b.monto - a.monto);
  }, [turnos]);
  const puede = permisos.data;
  /** Los turnos agrupados por tipo (mañana, tarde, noche…). */
  const grupos = useMemo(() => {
    const acumulado = new Map<string, TurnoLiquidacion[]>();

    for (const turno of [...turnos].sort((a, b) => a.id - b.id)) {
      const nombre = turno.tipo_turno?.nombre ?? 'No clasificado';
      const lista = acumulado.get(nombre) ?? [];

      lista.push(turno);
      acumulado.set(nombre, lista);
    }

    return [...acumulado.entries()].map(([nombre, lista]) => ({
      nombre,
      turnos: lista,
      ventas: lista.reduce(
        (suma, turno) => suma + Number(turno.resumen_financiero?.total_ventas_bruto ?? 0),
        0,
      ),
      extras: lista.reduce(
        (suma, turno) => suma + Number(turno.resumen_financiero?.total_lubricantes ?? 0),
        0,
      ),
      pendiente: lista.reduce(
        (suma, turno) => suma + Number(turno.resumen_financiero?.efectivo_pendiente ?? 0),
        0,
      ),
    }));
  }, [turnos]);

  const totales = useMemo(() => {
    const suma = (obtener: (turno: TurnoLiquidacion) => number) =>
      turnos.reduce((total, turno) => total + obtener(turno), 0);

    const pendientePorTurno = turnos.map((turno) => Number(turno.resumen_financiero?.efectivo_pendiente ?? 0));

    return {
      ventas: suma((turno) => Number(turno.resumen_financiero?.total_ventas_bruto ?? 0)),
      transacciones: suma((turno) => Number(turno.transacciones_series?.total_transacciones ?? 0)),
      bovedas: suma((turno) => Number(turno.resumen_financiero?.total_bovedas ?? 0)),
      gastos: suma((turno) => Number(turno.resumen_financiero?.total_gastos ?? 0)),
      lubricantes: suma((turno) => Number(turno.resumen_financiero?.total_lubricantes ?? 0)),
      descuentosVentasDirectas: suma(
        (turno) => Number(turno.resumen_financiero?.total_descuentos_sin_creditos ?? 0),
      ),
      descuentosCreditos: suma(
        (turno) => Number(turno.resumen_financiero?.total_descuentos_creditos ?? 0),
      ),
      creditos: suma((turno) => Number(turno.resumen_financiero?.total_creditos ?? 0)),
      adelantados: suma((turno) => Number(turno.resumen_financiero?.total_adelantados ?? 0)),
      ingresosAdicionales: suma(
        (turno) => Number(turno.resumen_financiero?.total_ingresos_adicionales ?? 0),
      ),
      // Neto del día: la suma de todas las diferencias (lo mismo que la última
      // columna del archivo exportado).
      neto: pendientePorTurno.reduce((total, valor) => total + valor, 0),
      faltante: pendientePorTurno
        .filter((valor) => valor > tolerancia)
        .reduce((total, valor) => total + valor, 0),
      sobrante: pendientePorTurno
        .filter((valor) => valor < -tolerancia)
        .reduce((total, valor) => total + Math.abs(valor), 0),
      cuadrados: pendientePorTurno.filter((valor) => Math.abs(valor) <= tolerancia).length,
    };
  }, [turnos, tolerancia]);

  /** Lo que suma y lo que resta al efectivo del día (como el cuadre del API). */
  const sumaDelDia = [
    { nombre: 'Ventas', monto: totales.ventas },
    { nombre: 'Lubricantes', monto: totales.lubricantes },
    ...(totales.ingresosAdicionales > 0
      ? [{ nombre: 'Ingresos adicionales', monto: totales.ingresosAdicionales }]
      : []),
    ...(Number(estadisticas.data?.total_gnv_monto ?? 0) > 0
      ? [{ nombre: 'GNV', monto: Number(estadisticas.data?.total_gnv_monto ?? 0) }]
      : []),
    ...(-totales.descuentosVentasDirectas > 0
      ? [{ nombre: 'Sobrecargos ventas directas', monto: -totales.descuentosVentasDirectas }]
      : []),
    ...(-totales.descuentosCreditos > 0
      ? [{ nombre: 'Ajuste por descuento de créditos', monto: -totales.descuentosCreditos }]
      : []),
  ];

  const restaDelDia = [
    ...metodosNoEfectivo.map((metodo) => ({
      nombre: metodo.metodo,
      monto: metodo.monto,
    })),
    { nombre: 'Bóvedas', monto: totales.bovedas },
    { nombre: 'Gastos', monto: totales.gastos },
    ...(totales.descuentosVentasDirectas >= 0
      ? [{ nombre: 'Descuentos ventas directas', monto: totales.descuentosVentasDirectas }]
      : []),
    ...(totales.descuentosCreditos >= 0
      ? [{ nombre: 'Descuentos créditos', monto: totales.descuentosCreditos }]
      : []),
    { nombre: 'Créditos', monto: totales.creditos },
    ...(totales.adelantados > 0
      ? [{ nombre: 'Pagos adelantados', monto: totales.adelantados }]
      : []),
  ];

  /** Estado del cuadre del día: define el color de la tarjeta y su etiqueta. */
  const estadoDelDia =
    totales.neto > tolerancia
      ? { tipo: 'faltante' as const, etiqueta: 'Faltante del día' }
      : totales.neto < -tolerancia
        ? { tipo: 'sobrante' as const, etiqueta: 'Sobrante del día' }
        : { tipo: 'cuadrado' as const, etiqueta: 'Día cuadrado' };

  /** Ejecuta una acción de escritura y refresca el día. */
  const ejecutar = async (accion: () => Promise<unknown>, alTerminar?: () => void) => {
    setAviso(null);
    setOcupado(true);

    try {
      await accion();
      await consulta.refetch();
      await estadisticas.refetch();
      alTerminar?.();
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo completar la acción.');
    } finally {
      setOcupado(false);
    }
  };

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarTurnos(fecha), {
        nombre: `liquidacion-turnos-${fecha}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el día.');
    }
  };

  const exportarTransacciones = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarTransacciones(fecha), {
        nombre: `transacciones-${fecha}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudieron exportar las transacciones.');
    }
  };

  /** Parte diario del día en PDF (lo genera la API del panel; ya no el módulo anterior). */
  const abrirParteDiario = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.parteDiario(fecha));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el parte diario.');
    }
  };

  /** Ticket de cierre de un turno en PDF (recibo de 80 mm). */
  const abrirTicket = async (turnoId: number) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.ticketTurno(turnoId));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el ticket del turno.');
    }
  };

  /** Guarda el «monto mínimo faltante» de la liquidación. */
  const guardarConfiguracion = () => {
    void ejecutar(
      () => reportesApi.guardarConfiguracionLiquidacion(Number(montoMinimo || 0)),
      () => {
        setConfigAbierta(false);
        setAviso(`Monto mínimo faltante actualizado a ${formatCurrency(Number(montoMinimo || 0))}.`);
      },
    );
  };

  /** Fila de un concepto del cuadre dentro de la tarjeta del turno. */
  const concepto = (etiqueta: string, valor: number | null | undefined, resaltar = false) => (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-ink-500">{etiqueta}</span>
      <span className={cn('text-xs', resaltar ? 'font-semibold text-ink-800' : 'text-ink-700')}>
        {formatCurrency(valor ?? 0)}
      </span>
    </div>
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Turnos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Liquidación del día: ventas, bóvedas, gastos y el cuadre de cada turno.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Fecha</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={fecha}
                  onChange={(evento) => setFecha(evento.target.value || fechaLocal(new Date()))}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <div className="grid grid-cols-2 items-end gap-2 sm:col-span-2 sm:grid-cols-3 xl:grid-cols-4">
              <Button
                fullWidth
                className="px-3 text-xs whitespace-nowrap"
                variant="outline"
                onClick={() => setFecha(fechaLocal(new Date()))}
                leftIcon={<Search className="size-4" aria-hidden="true" />}
              >
                Hoy
              </Button>

              <Button
                fullWidth
                className="px-3 text-xs whitespace-nowrap"
                variant="outline"
                onClick={() => setConsolidadoAbierto(true)}
                leftIcon={<Calculator className="size-4" aria-hidden="true" />}
              >
                Consolidado
              </Button>

              <Button
                fullWidth
                className="px-3 text-xs whitespace-nowrap"
                variant="outline"
                onClick={exportarTransacciones}
                leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
              >
                Transacciones
              </Button>

              <Button
                fullWidth
                className="px-3 text-xs whitespace-nowrap"
                variant="outline"
                onClick={() => void abrirParteDiario()}
                leftIcon={<FileText className="size-4" aria-hidden="true" />}
              >
                Parte diario
              </Button>

              <Button
                fullWidth
                className="px-3 text-xs whitespace-nowrap"
                variant="outline"
                onClick={() => {
                  setMontoMinimo(String(tolerancia));
                  setConfigAbierta(true);
                }}
                leftIcon={<Settings className="size-4" aria-hidden="true" />}
              >
                Configuración
              </Button>

              {puede?.verificar_masivo ? (
                <Button
                  fullWidth
                  className="px-3 text-xs whitespace-nowrap"
                  variant="outline"
                  loading={ocupado}
                  onClick={() =>
                    void ejecutar(() => reportesApi.verificarMasivo(fecha), () =>
                      setAviso('Turnos del día verificados.'),
                    )
                  }
                  leftIcon={<Check className="size-4" aria-hidden="true" />}
                >
                  Verificar todos
                </Button>
              ) : null}

              {puede?.deshacer_masivo ? (
                <Button
                  fullWidth
                  className="px-3 text-xs whitespace-nowrap"
                  variant="outline"
                  loading={ocupado}
                  onClick={() =>
                    void ejecutar(() => reportesApi.deshacerMasivo(fecha), () =>
                      setAviso('Verificaciones del día deshechas.'),
                    )
                  }
                  leftIcon={<RotateCcw className="size-4" aria-hidden="true" />}
                >
                  Deshacer todos
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar la liquidación">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="info" title="Aviso">
          {aviso}
        </Alert>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!consulta.isLoading && turnos.length === 0 ? (
        <Alert variant="info" title="No hay turnos en la fecha elegida">
          Elige otra fecha en el calendario: en esta copia los turnos están entre el 23 de octubre de
          2025 y el 2 de septiembre de 2026.
        </Alert>
      ) : null}

      {turnos.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                etiqueta: 'Turnos',
                valor: formatNumber(estadisticas.data?.total_turnos ?? turnos.length),
                detalle: `${formatNumber(estadisticas.data?.turnos_verificados ?? 0)} verificado(s) · ${formatNumber(
                  estadisticas.data?.turnos_pendientes ?? 0,
                )} pendiente(s)`,
              },
              {
                etiqueta: 'Ventas del día',
                valor: formatCurrency(totales.ventas),
                detalle: `${formatNumber(totales.transacciones)} transacción(es) · ${formatNumber(
                  estadisticas.data?.total_trabajadores ?? 0,
                )} trabajador(es)`,
              },
              {
                etiqueta: 'Bóvedas y gastos',
                valor: formatCurrency(totales.bovedas + totales.gastos),
                detalle: `Bóvedas ${formatCurrency(totales.bovedas)} · Gastos ${formatCurrency(
                  totales.gastos,
                )}`,
              },
              {
                etiqueta: estadoDelDia.etiqueta,
                valor: formatCurrency(totales.neto),
                detalle: `${formatNumber(totales.cuadrados)} cuadrado(s) · faltante ${formatCurrency(
                  totales.faltante,
                )} · sobrante ${formatCurrency(totales.sobrante)}`,
                tono: estadoDelDia.tipo,
              },
            ].map((tarjeta) => {
              const tono = TONOS_CUADRE[tarjeta.tono ?? 'neutro'];

              return (
                <Card key={tarjeta.etiqueta} className={tono.caja}>
                  <CardContent className="p-4">
                    <p
                      className={cn(
                        'flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase',
                        tono.etiqueta,
                      )}
                    >
                      {tono.icono}
                      {tarjeta.etiqueta}
                    </p>
                    <p className={cn('text-lg font-bold', tono.valor)}>{tarjeta.valor}</p>
                    <p className={cn('text-xs', tono.detalle)}>{tarjeta.detalle}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardContent className="space-y-3 p-4">
              <div>
                <p className="text-sm font-semibold text-ink-800">Cuadre del día</p>
                <p className="text-xs text-ink-500">
                  Lo que suma y lo que resta al efectivo del día.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-card border border-emerald-200 bg-emerald-50/60 p-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-emerald-700 uppercase">
                    <Plus className="size-3.5" aria-hidden="true" />
                    Suma al efectivo
                  </p>
                  <dl className="mt-2 space-y-1">
                    {sumaDelDia.map((concepto) => (
                      <div
                        key={concepto.nombre}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <dt className="text-xs text-ink-600">{concepto.nombre}</dt>
                        <dd className="text-sm font-semibold text-ink-900 tabular-nums">
                          {formatCurrency(concepto.monto)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="rounded-card border border-rose-200 bg-rose-50/50 p-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-rose-700 uppercase">
                    <Minus className="size-3.5" aria-hidden="true" />
                    Resta al efectivo
                  </p>
                  <dl className="mt-2 space-y-1">
                    {restaDelDia.map((concepto) => (
                      <div
                        key={concepto.nombre}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <dt className="text-xs text-ink-600">{concepto.nombre}</dt>
                        <dd className="text-sm font-semibold text-ink-900 tabular-nums">
                          {formatCurrency(concepto.monto)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {grupos.map((grupo) => (
              <div key={grupo.nombre} className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 bg-ink-50/60 px-4 py-2">
                  <span className="text-sm font-semibold text-ink-800">{grupo.nombre}</span>
                  <span className="flex flex-wrap items-center gap-x-4 text-xs text-ink-600">
                    <span>{formatNumber(grupo.turnos.length)} turno(s)</span>
                    <span>Ventas {formatCurrency(grupo.ventas)}</span>
                    <span>Extras {formatCurrency(grupo.extras)}</span>
                    <span
                      className={cn(
                        'font-semibold',
                        Math.abs(grupo.pendiente) <= tolerancia
                          ? 'text-emerald-700'
                          : grupo.pendiente > 0
                            ? 'text-rose-600'
                            : 'text-amber-700',
                      )}
                    >
                      {Math.abs(grupo.pendiente) <= tolerancia
                        ? 'Cuadrado'
                        : grupo.pendiente > 0
                          ? `Faltante ${formatCurrency(grupo.pendiente)}`
                          : `Sobrante ${formatCurrency(Math.abs(grupo.pendiente))}`}
                    </span>
                  </span>
                </div>

                <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                  {grupo.turnos.map((turno) => {
                    const resumen = turno.resumen_financiero;
                    const estado = estadoDeVerificacion(turno);
                    const marca = diferencia(turno, tolerancia);

                    return (
                      <Card key={turno.id}>
                        <CardContent className="space-y-3 p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-ink-900">{nombreDe(turno)}</p>
                              <p className="text-xs text-ink-500">
                                {hora(turno.hora_llegada)} – {hora(turno.hora_salida)} · turno #
                                {turno.id}
                              </p>
                            </div>
                            <Badge variant={estado.tono}>{estado.texto}</Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {concepto('Ventas', resumen.total_ventas_bruto, true)}
                            {concepto('Lubricantes', resumen.total_lubricantes)}
                            {concepto('Bóvedas', resumen.total_bovedas)}
                            {concepto('Gastos', resumen.total_gastos)}
                            {resumen.metodos_pago_no_efectivo.map((metodo) =>
                              concepto(formatPaymentMethodName(metodo.metodo), metodo.monto),
                            )}
                            {concepto('Créditos', resumen.total_creditos)}
                            {concepto(
                              'Descuentos ventas directas',
                              resumen.total_descuentos_sin_creditos,
                            )}
                            {concepto(
                              'Descuentos créditos',
                              resumen.total_descuentos_creditos,
                            )}
                            {concepto('Pagos adelantados', resumen.total_adelantados)}
                            {Number(turno.monto_gnv ?? 0) > 0
                              ? concepto('GNV', Number(turno.monto_gnv))
                              : null}
                          </div>

                          <div className="space-y-1 border-t border-ink-100 pt-2">
                            <div className="flex items-baseline justify-between">
                              <span className="text-xs font-semibold text-ink-700">
                                Efectivo esperado
                              </span>
                              <span className="text-sm font-bold text-ink-900">
                                {formatCurrency(resumen.efectivo_calculado)}
                              </span>
                            </div>

                            <div className="flex items-baseline justify-between">
                              <span className="text-xs font-semibold text-ink-700">
                                {marca.tipo === 'faltante'
                                  ? 'Faltante'
                                  : marca.tipo === 'sobrante'
                                    ? 'Sobrante'
                                    : 'Cuadrado'}
                              </span>
                              <span
                                className={cn(
                                  'text-sm font-bold',
                                  marca.tipo === 'cuadrado'
                                    ? 'text-emerald-700'
                                    : marca.tipo === 'faltante'
                                      ? 'text-rose-600'
                                      : 'text-amber-700',
                                )}
                              >
                                {formatCurrency(marca.monto)}
                              </span>
                            </div>

                            <p className="text-[11px] text-ink-400">
                              {formatNumber(resumen.estadisticas.total_transacciones)} transacción(es) ·{' '}
                              {formatNumber(resumen.estadisticas.volumen_total)} de volumen
                              {tolerancia > 0
                                ? ` · margen ${formatCurrency(tolerancia)}`
                                : ' · sin margen'}
                            </p>
                          </div>

                          <div className="grid gap-2 pt-1 sm:grid-cols-3">
                            <Button
                              fullWidth
                              className="text-xs whitespace-nowrap"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setTurnoTransacciones(turno);
                                setDetalle('');
                              }}
                              leftIcon={<FileSpreadsheet className="size-3.5" aria-hidden="true" />}
                            >
                              Trans. ({formatNumber(resumen.estadisticas.total_transacciones)})
                            </Button>

                            <Button
                              fullWidth
                              className="text-xs whitespace-nowrap"
                              variant="outline"
                              size="sm"
                              onClick={() => setTurnoCalculos(turno)}
                              leftIcon={<Calculator className="size-3.5" aria-hidden="true" />}
                            >
                              Cálculos
                            </Button>

                            <Button
                              fullWidth
                              className="text-xs whitespace-nowrap"
                              variant="outline"
                              size="sm"
                              onClick={() => setTurnoAjustes(turno)}
                              leftIcon={<Vault className="size-3.5" aria-hidden="true" />}
                            >
                              Bóvedas · Gastos
                            </Button>

                            <Button
                              fullWidth
                              className="text-xs whitespace-nowrap"
                              variant="outline"
                              size="sm"
                              onClick={() => setTurnoMetodos(turno)}
                              leftIcon={<CreditCard className="size-3.5" aria-hidden="true" />}
                            >
                              Métodos de pago
                            </Button>

                            <Button
                              fullWidth
                              className="text-xs whitespace-nowrap"
                              variant="outline"
                              size="sm"
                              onClick={() => void abrirTicket(turno.id)}
                              leftIcon={<FileText className="size-3.5" aria-hidden="true" />}
                            >
                              Ticket
                            </Button>

                            {puede?.verificar && Number(turno.estado_verificacion) !== 2 ? (
                              <Button
                                fullWidth
                                className="text-xs whitespace-nowrap"
                                size="sm"
                                onClick={() => {
                                  setTurnoAVerificar(turno);
                                  setEfectivoRecibido(resumen.efectivo_calculado.toFixed(2));
                                  setDetalle('');
                                }}
                              >
                                Verificar
                              </Button>
                            ) : null}

                            {puede?.deshacer_verificacion && Number(turno.estado_verificacion) === 2 ? (
                              <Button
                                fullWidth
                                className="text-xs whitespace-nowrap"
                                size="sm"
                                variant="danger"
                                loading={ocupado}
                                onClick={() =>
                                  void ejecutar(() => reportesApi.deshacerVerificacion(turno.id))
                                }
                              >
                                Deshacer
                              </Button>
                            ) : null}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <p className="flex items-start gap-2 text-xs text-ink-500">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            El cuadre suma las transacciones del controlador, los lubricantes, los ingresos
            adicionales y el GNV, y descuenta bóvedas, gastos, cobros que no son efectivo,
            descuentos, créditos, consumo interno, canjes y pagos adelantados. La exportación trae
            una fila por turno con el mismo detalle.
          </p>
        </>
      ) : null}

      {/* Detalle de transacciones del turno (buscador, tipo y productividad por lado) */}
      <TurnoTransaccionesDialog
        turno={turnoTransacciones}
        onClose={() => setTurnoTransacciones(null)}
      />

      {/* Verificación del turno */}
      <Dialog
        open={turnoAVerificar !== null}
        onClose={() => setTurnoAVerificar(null)}
        title="Verificar turno"
        description={
          turnoAVerificar
            ? `${nombreDe(turnoAVerificar)} · efectivo esperado ${formatCurrency(
                turnoAVerificar.resumen_financiero.efectivo_calculado,
              )}`
            : ''
        }
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setTurnoAVerificar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={ocupado}
              onClick={() => {
                const turno = turnoAVerificar;

                if (!turno) return;

                if (!detalle.trim()) {
                  setAviso('Para rechazar un turno indica el detalle de lo que faltó o sobró.');
                  return;
                }

                void ejecutar(
                  () =>
                    reportesApi.rechazarTurno({
                      turno_id: turno.id,
                      usuario_id: turno.usuario_id,
                      efectivo_recibido: Number(efectivoRecibido || 0),
                      efectivo_esperado: turno.resumen_financiero.efectivo_calculado,
                      detalle,
                    }),
                  () => {
                    setTurnoAVerificar(null);
                    setAviso(`Turno ${turno.id} rechazado.`);
                  },
                );
              }}
              leftIcon={<X className="size-4" aria-hidden="true" />}
            >
              Rechazar
            </Button>
            <Button
              loading={ocupado}
              onClick={() => {
                const turno = turnoAVerificar;

                if (!turno) return;

                void ejecutar(
                  () =>
                    reportesApi.verificarTurno({
                      turno_id: turno.id,
                      usuario_id: turno.usuario_id,
                      efectivo_recibido: Number(efectivoRecibido || 0),
                      efectivo_esperado: turno.resumen_financiero.efectivo_calculado,
                      detalle,
                    }),
                  () => {
                    setTurnoAVerificar(null);
                    setAviso(`Turno ${turno.id} verificado.`);
                  },
                );
              }}
              leftIcon={<Check className="size-4" aria-hidden="true" />}
            >
              Verificar
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {turnoAVerificar ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-card bg-ink-50/60 p-3 text-xs text-ink-600">
              <span>Ventas: {formatCurrency(turnoAVerificar.resumen_financiero.total_ventas_bruto)}</span>
              <span>Bóvedas: {formatCurrency(turnoAVerificar.resumen_financiero.total_bovedas)}</span>
              <span>Gastos: {formatCurrency(turnoAVerificar.resumen_financiero.total_gastos)}</span>
              {turnoAVerificar.resumen_financiero.metodos_pago_no_efectivo.map((metodo) => (
                <span key={metodo.metodo}>
                  {formatPaymentMethodName(metodo.metodo)}:{' '}
                  {formatCurrency(metodo.monto)}
                </span>
              ))}
              <span className="font-semibold text-ink-800">
                Efectivo esperado: {formatCurrency(turnoAVerificar.resumen_financiero.efectivo_calculado)}
              </span>
              <span
                className={cn(
                  'font-semibold',
                  diferencia(turnoAVerificar, 0.1).tipo === 'cuadrado'
                    ? 'text-emerald-700'
                    : 'text-rose-600',
                )}
              >
                {diferencia(turnoAVerificar, 0.1).tipo === 'faltante'
                  ? 'Faltante'
                  : diferencia(turnoAVerificar, 0.1).tipo === 'sobrante'
                    ? 'Sobrante'
                    : 'Cuadrado'}
                : {formatCurrency(diferencia(turnoAVerificar, 0.1).monto)}
              </span>
            </div>
          ) : null}

          <Input
            label="Efectivo recibido"
            type="number"
            step="0.01"
            value={efectivoRecibido}
            onChange={(evento) => setEfectivoRecibido(evento.target.value)}
            leftIcon={<Users className="size-4" aria-hidden="true" />}
          />

          <Input
            label="Detalle de la verificación"
            value={detalle}
            onChange={(evento) => setDetalle(evento.target.value)}
            hint="Se guarda junto a la verificación (por ejemplo, quién entregó el efectivo)."
          />
        </div>
      </Dialog>

      {/* Cálculos del turno (detalles, resumen y descuentos del anterior) */}
      <TurnoCalculosDialog turno={turnoCalculos} onClose={() => setTurnoCalculos(null)} />

      {/* Métodos de pago del turno (cobros que no son efectivo) */}
      <TurnoMetodosPagoDialog
        turno={turnoMetodos}
        permisos={puede}
        onClose={() => setTurnoMetodos(null)}
        onCambio={async () => {
          const [turnosActualizados] = await Promise.all([
            consulta.refetch(),
            estadisticas.refetch(),
          ]);
          const turnoActualizado = turnosActualizados.data?.find(
            (turno) => turno.id === turnoMetodos?.id,
          );
          if (turnoActualizado) setTurnoMetodos(turnoActualizado);
        }}
      />

      {/* Bóvedas, gastos y GNV del turno */}
      <TurnoAjustesDialog
        turno={turnoAjustes}
        permisos={puede}
        onClose={() => setTurnoAjustes(null)}
        onCambio={async () => {
          const [turnosActualizados] = await Promise.all([
            consulta.refetch(),
            estadisticas.refetch(),
          ]);
          const turnoActualizado = turnosActualizados.data?.find(
            (turno) => turno.id === turnoAjustes?.id,
          );
          if (turnoActualizado) setTurnoAjustes(turnoActualizado);
        }}
      />

      {/* Consolidado del día */}
      <TurnoConsolidadoDialog
        turnos={turnos}
        fecha={fecha}
        abierto={consolidadoAbierto}
        onClose={() => setConsolidadoAbierto(false)}
      />

      {/* Configuración de la liquidación (monto mínimo faltante) */}
      <Dialog
        open={configAbierta}
        onClose={() => setConfigAbierta(false)}
        title="Configuración de la liquidación"
        description="Monto mínimo faltante y categorías de turno."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfigAbierta(false)}>
              Cancelar
            </Button>
            <Button loading={ocupado} onClick={guardarConfiguracion}>
              Guardar
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <Input
            label="Monto mínimo faltante (S/)"
            type="number"
            step="0.01"
            value={montoMinimo}
            onChange={(evento) => setMontoMinimo(evento.target.value)}
            hint="Por debajo de este importe el turno se considera cuadrado. Se guarda en la configuración de la empresa (0 a 3 soles)."
          />

          <div>
            <p className="text-xs font-semibold text-ink-700">Categorías de turno</p>
            <ul className="mt-1 space-y-1 text-xs text-ink-600">
              {(configuracion.data?.categorias_turnos ?? []).map((categoria) => (
                <li key={categoria.id} className="flex flex-wrap items-center gap-2">
                  <BadgePercent className="size-3.5 text-ink-400" aria-hidden="true" />
                  <span className="font-medium text-ink-800">{categoria.nombre}</span>
                  <span>
                    {categoria.inicio.slice(0, 5)} – {categoria.fin.slice(0, 5)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
